import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { OrderReservationService } from 'src/app/services/order-reservation.service';
import { ResponseObject } from 'src/app/models/response-object.model';
import { OrderReservationStatus } from 'src/app/enums/order-reservation-status.enum';
import { switchMap } from 'rxjs/operators';
import { throwError } from 'rxjs';

@Component({
  selector: 'app-grouped-detail-order-reservation',
  templateUrl: './grouped-detail-order-reservation.component.html',
  styleUrls: ['./grouped-detail-order-reservation.component.scss']
})
export class GroupedDetailOrderReservationComponent {

  @Input() showGroupedModal: boolean = false;

  @Input() groupedLoading: boolean = false;

  @Input() groupedReservations: {
    warehouseId: number | null;
    orderReservations: any[];
    orderReservationProducts: any[];
  }[] = [];

  @Output() closed = new EventEmitter<boolean>();

  statuses = OrderReservationStatus;

  statusOptions = [
    { key: 'RESERVED', label: OrderReservationStatus.RESERVED, color: 'bg-info' },
    { key: 'DONE', label: OrderReservationStatus.DONE, color: 'bg-success' },
    { key: 'TO_CONFIRMED', label: OrderReservationStatus.TO_CONFIRMED, color: 'bg-warning' },
    { key: 'EXPIRED', label: OrderReservationStatus.EXPIRED, color: 'bg-danger' },
    { key: 'CANCELLED', label: OrderReservationStatus.CANCELLED, color: 'bg-secondary' },
  ];

  constructor(
    private toastr: ToastrService,
    private reservationService: OrderReservationService
  ) { }

  close(modal?: any) {
    this.closed.emit();
  }

  getStatusColor(status: string): string {
    const option = this.statusOptions.find(s => s.label === status);
    return option ? option.color : 'bg-secondary';
  }

  toggleBreakdown(product: any, group: any) {
    product.showBreakdown = !product.showBreakdown;
    if (product.showBreakdown && !product.warehouseExistence) {
      this.loadWarehouseExistence(product);
    }
  }

  loadWarehouseExistence(product: any) {
    if (!product?.product?.id) return;

    product.loadingExistence = true;

    this.reservationService.getAllWarehouseExistence(product.product.id).subscribe({
      next: (response: ResponseObject) => {
        product.loadingExistence = false;
        product.warehouseExistence = (response.data || [])
          .filter((w: any) => Number(w.existence) > 0)
          .map((w: any) => ({
            ...w,
            transferQuantity: 0
          }));
      },
      error: (err) => {
        product.loadingExistence = false;
        this.toastr.error('Error al cargar existencias del producto');
      }
    });
  }

  // validateTransferQuantity1(product: any, warehouse: any) {
  //   if (warehouse.transferQuantity > warehouse.existence) {
  //     warehouse.transferQuantity = warehouse.existence;
  //     this.toastr.warning('No puede pasar más de la existencia disponible.');
  //   }

  //   const totalAssigned = (product.warehouseExistence || [])
  //     .map((w: any) => Number(w.transferQuantity) || 0)
  //     .reduce((a: number, b: number) => a + b, 0);

  //   if (totalAssigned > product.quantity) {
  //     warehouse.transferQuantity = Math.max(
  //       0,
  //       product.quantity - (totalAssigned - warehouse.transferQuantity)
  //     );
  //     this.toastr.warning('La cantidad total no puede exceder la reservada.');
  //   }
  // }

  validateTransferQuantity(product: any, warehouse: any) {
    if (warehouse.transferQuantity > warehouse.existence) {
      this.toastr.warning('No puede pasar más de la existencia disponible.');
      warehouse.transferQuantity = warehouse.existence;
    }

    const totalAssigned = (product.warehouseExistence || [])
      .map((w: any) => Number(w.transferQuantity) || 0)
      .reduce((a: number, b: number) => a + b, 0);

    if (totalAssigned > product.quantity) {
      this.toastr.warning(
        `El producto "${product.product?.name}" excede la cantidad reservada (${totalAssigned}/${product.quantity}).`
      );
    }
  }


  getTotalTransferQuantity(product: any): number {
    if (!product.warehouseExistence) return 0;
    return product.warehouseExistence
      .map((w: any) => Number(w.transferQuantity) || 0)
      .reduce((a: number, b: number) => a + b, 0);
  }

  buildGroupedTransferRequest(): any {
    if (!this.groupedReservations || this.groupedReservations.length === 0) {
      return null;
    }

    const group = this.groupedReservations[0];

    const request = {
      orderReservations: (group.orderReservations || []).map((o: any) => o.id),
      products: (group.orderReservationProducts || []).map((p: any) => ({
        productId: p.product?.id,
        totalQuantity: p.quantity,
        fromWarehouses:
          p.warehouseExistence
            ?.filter((w: any) => w.transferQuantity > 0)
            .map((w: any) => ({
              warehouseId: w.warehouseId,
              quantity: w.transferQuantity
            })) || []
      }))
    };

    return request;
  }


  validateBeforeConfirm(): boolean {
    let valid = true;

    for (const group of this.groupedReservations) {
      for (const prod of group.orderReservationProducts || []) {
        const totalAssigned = this.getTotalTransferQuantity(prod);
        const expected = Number(prod.quantity);

        if (totalAssigned < expected) {
          this.toastr.warning(
            `El producto "${prod.product?.name}" tiene cantidad incompleta (${totalAssigned}/${expected}).`
          );
          valid = false;
        } else if (totalAssigned > expected) {
          this.toastr.warning(
            `El producto "${prod.product?.name}" excede la cantidad reservada (${totalAssigned}/${expected}).`
          );
          valid = false;
        }
      }
    }

    return valid;
  }


  confirmGroupedTransfer() {
    if (!this.validateBeforeConfirm()) {
      this.toastr.error('Verifique las cantidades antes de continuar.');
      return;
    }

    const warehouseId = this.groupedReservations[0].orderReservations[0].warehouseData.id;

    const payload = this.buildGroupedTransferRequest();
    this.reservationService.transferProductValidation(warehouseId, new Date().toLocaleDateString('en-CA')).pipe(
      switchMap((validateRes: any) => {
        if (!validateRes.data) {
          this.toastr.error('La Bodega no tiene Destino Asignado.');
          return throwError(() => new Error("No hay destino asignado para la transferencia, por favor asigne uno antes de realizar la transferencia"));
        } else {
          return this.reservationService.transferOrderReservationGrouped(payload)
        }
      }),
    ).subscribe({
      next: (response) => {
        console.log(response);
        this.toastr.success('Transferencias realizada correctamente.')
        this.closed.emit(true);
      },
      error: (err) => this.toastr.error('Error al realizar la transferencias.')
    });
  }

}
